/**
 * Date Utilities
 *
 * Handles date parsing and formatting with timezone safety.
 *
 * CRITICAL: JavaScript's new Date("YYYY-MM-DD") parses as UTC midnight,
 * which shifts the date backward in timezones behind UTC (like US).
 * These utilities ensure dates are parsed as LOCAL time.
 */

/**
 * Parse a date string (YYYY-MM-DD) as LOCAL time.
 *
 * Uses noon (12:00) to avoid daylight saving time edge cases
 * that could shift the date at midnight boundaries.
 *
 * @param dateString - Date in YYYY-MM-DD format (from HTML date input)
 * @returns Date object at noon local time on the specified day
 *
 * @example
 * // User selects "2025-12-14" in date picker
 * const date = parseDateAsLocal("2025-12-14");
 * // Returns Dec 14, 2025 12:00:00 local time (NOT UTC!)
 */
export function parseDateAsLocal(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);

  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    console.warn('[DATE] Invalid date string, using current date:', dateString);
    return new Date();
  }

  // Create date at noon local time to avoid DST edge cases
  // month - 1 because JavaScript months are 0-indexed
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/**
 * Format a Date object to YYYY-MM-DD string for HTML date inputs.
 *
 * @param date - Date object
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date as a YYYY-MM-DD string (local time).
 *
 * @returns Today's date in YYYY-MM-DD format
 */
export function getTodayString(): string {
  return formatDateForInput(new Date());
}

/**
 * Get yesterday's date as YYYY-MM-DD string (local time).
 */
export function getYesterdayString(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return formatDateForInput(d);
}

/**
 * Check if two dates represent the same calendar day (local time).
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
